import "@/App.css";
import { motion, AnimatePresence } from "framer-motion";
import { useState, useEffect, useCallback } from "react";
import { 
  CheckCircle, 
  Zap, 
  FileCheck, 
  Users, 
  Phone, 
  Mail, 
  MapPin,
  Menu,
  X,
  ChevronRight,
  ChevronDown,
  Building2,
  Shield,
  ClipboardCheck,
  Home,
  Handshake,
  Search,
  ArrowRight,
  Download,
  ExternalLink,
  Cookie,
  Settings,
  Activity,
  Car
} from "lucide-react";

// ==========================================
// GOOGLE ANALYTICS & COOKIE CONSENT SYSTEM
// ==========================================

const GA_MEASUREMENT_ID = 'G-LPYR5SDSVE';
const COOKIE_CONSENT_KEY = 'enthida_cookie_consent';
const COOKIE_DOMAIN = '.enthida.de'; // For cross-subdomain tracking

// Cookie Consent Utility Functions
const getCookieConsent = () => {
  if (typeof window === 'undefined') return null;
  const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
  if (consent) {
    try {
      return JSON.parse(consent);
    } catch {
      return null;
    }
  }
  return null;
};

const setCookieConsent = (consent) => {
  if (typeof window === 'undefined') return;
  const consentData = {
    ...consent,
    timestamp: new Date().toISOString(),
    version: '1.0'
  };
  localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consentData));
  
  // Also set a cookie for cross-subdomain access
  const expiryDate = new Date();
  expiryDate.setFullYear(expiryDate.getFullYear() + 1);
  document.cookie = `${COOKIE_CONSENT_KEY}=${JSON.stringify(consentData)}; expires=${expiryDate.toUTCString()}; path=/; domain=${COOKIE_DOMAIN}; SameSite=Lax`;
};

// Initialize Google Analytics (only when consent is given)
const initializeGA = () => {
  if (typeof window === 'undefined') return;
  
  // Check if already initialized
  if (window.gaInitialized) return;
  
  // Load gtag.js script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);
  
  // Initialize gtag
  window.dataLayer = window.dataLayer || [];
  function gtag() { window.dataLayer.push(arguments); }
  window.gtag = gtag;
  
  gtag('js', new Date());
  
  // Configure GA4 with cross-subdomain tracking
  gtag('config', GA_MEASUREMENT_ID, {
    'cookie_domain': 'enthida.de',
    'cookie_flags': 'SameSite=None;Secure',
    'anonymize_ip': true, // GDPR requirement
    'allow_google_signals': false, // Disable remarketing for GDPR
    'allow_ad_personalization_signals': false, // Disable ad personalization for GDPR
    'linker': {
      'domains': ['enthida.de', 'partner.enthida.de', 'wartung.enthida.de', 'leistung.enthida.de', 'checkliste.enthida.de', 'abnahme.enthida.de', 'gewerbe.enthida.de'],
      'accept_incoming': true
    }
  });
  
  window.gaInitialized = true;
  console.log('Google Analytics initialized with consent');
};

// Disable GA tracking
const disableGA = () => {
  if (typeof window === 'undefined') return;
  window[`ga-disable-${GA_MEASUREMENT_ID}`] = true;
  
  // Remove GA cookies
  document.cookie = '_ga=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.enthida.de;';
  document.cookie = '_ga_*=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.enthida.de;';
  
  console.log('Google Analytics disabled');
};

// Cookie Consent Banner Component
const CookieConsentBanner = ({ onConsentChange }) => {
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always required
    analytics: false,
  });

  useEffect(() => {
    const consent = getCookieConsent();
    if (!consent) {
      // Show banner after a short delay
      setTimeout(() => setShowBanner(true), 1000);
    } else {
      // Apply saved consent
      if (consent.analytics) {
        initializeGA();
      }
      onConsentChange && onConsentChange(consent);
    }
  }, [onConsentChange]);

  const handleAcceptAll = () => {
    const consent = { necessary: true, analytics: true };
    setCookieConsent(consent);
    initializeGA();
    setShowBanner(false);
    onConsentChange && onConsentChange(consent);
  };

  const handleAcceptNecessary = () => {
    const consent = { necessary: true, analytics: false };
    setCookieConsent(consent);
    disableGA();
    setShowBanner(false);
    onConsentChange && onConsentChange(consent);
  };

  const handleSavePreferences = () => {
    const consent = { ...preferences };
    setCookieConsent(consent);
    if (consent.analytics) {
      initializeGA();
    } else {
      disableGA();
    }
    setShowBanner(false);
    setShowDetails(false);
    onConsentChange && onConsentChange(consent);
  };

  if (!showBanner) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        className="fixed bottom-0 left-0 right-0 z-[200] p-4"
      >
        <div className="max-w-4xl mx-auto bg-slate-900/98 backdrop-blur-lg border border-white/10 shadow-2xl rounded-lg overflow-hidden">
          {!showDetails ? (
            // Simple View
            <div className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex-shrink-0 flex items-center justify-center bg-neon-green/10 rounded-lg">
                  <Cookie size={24} className="text-neon-green" />
                </div>
                <div className="flex-1">
                  <h3 className="font-sora text-lg font-semibold text-white mb-2">
                    Cookie-Einstellungen
                  </h3>
                  <p className="text-slate-400 text-sm mb-4">
                    Wir verwenden Cookies, um Ihre Erfahrung zu verbessern und unsere Website zu analysieren. 
                    Sie können wählen, welche Cookies Sie akzeptieren möchten.
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <button
                      onClick={handleAcceptAll}
                      className="px-5 py-2.5 bg-neon-green text-dark font-semibold rounded hover:bg-neon-green-hover transition-colors"
                    >
                      Alle akzeptieren
                    </button>
                    <button
                      onClick={handleAcceptNecessary}
                      className="px-5 py-2.5 bg-slate-700 text-white font-semibold rounded hover:bg-slate-600 transition-colors"
                    >
                      Nur notwendige
                    </button>
                    <button
                      onClick={() => setShowDetails(true)}
                      className="px-5 py-2.5 text-slate-400 hover:text-white font-medium transition-colors flex items-center gap-2"
                    >
                      <Settings size={16} />
                      Einstellungen
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            // Detailed View
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-sora text-lg font-semibold text-white flex items-center gap-2">
                  <Settings size={20} className="text-neon-green" />
                  Cookie-Einstellungen
                </h3>
                <button
                  onClick={() => setShowDetails(false)}
                  className="text-slate-400 hover:text-white"
                >
                  <X size={20} />
                </button>
              </div>
              
              <div className="space-y-4 mb-6">
                {/* Necessary Cookies */}
                <div className="p-4 bg-slate-800/50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-white">Notwendige Cookies</span>
                    <span className="px-2 py-1 bg-neon-green/20 text-neon-green text-xs rounded">Immer aktiv</span>
                  </div>
                  <p className="text-slate-400 text-sm">
                    Diese Cookies sind für die Grundfunktionen der Website erforderlich und können nicht deaktiviert werden.
                  </p>
                </div>
                
                {/* Analytics Cookies */}
                <div className="p-4 bg-slate-800/50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-white">Analyse-Cookies (Google Analytics)</span>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={preferences.analytics}
                        onChange={(e) => setPreferences({ ...preferences, analytics: e.target.checked })}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-slate-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-neon-green"></div>
                    </label>
                  </div>
                  <p className="text-slate-400 text-sm">
                    Diese Cookies helfen uns zu verstehen, wie Besucher mit unserer Website interagieren. 
                    Alle Daten werden anonymisiert erfasst.
                  </p>
                </div>
              </div>
              
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={handleSavePreferences}
                  className="px-5 py-2.5 bg-neon-green text-dark font-semibold rounded hover:bg-neon-green-hover transition-colors"
                >
                  Einstellungen speichern
                </button>
                <button
                  onClick={handleAcceptAll}
                  className="px-5 py-2.5 bg-slate-700 text-white font-semibold rounded hover:bg-slate-600 transition-colors"
                >
                  Alle akzeptieren
                </button>
              </div>
              
              <p className="mt-4 text-xs text-slate-500">
                Weitere Informationen finden Sie in unserer <button onClick={() => {}} className="text-neon-turquoise hover:underline">Datenschutzerklärung</button>.
              </p>
            </div>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

// Remove Emergent watermark/badge
const removeWatermark = () => {
  // Target the specific badge by ID
  const badge = document.getElementById('emergent-badge');
  if (badge) {
    badge.style.display = 'none';
    badge.remove();
  }
  
  // Also find by href
  document.querySelectorAll('a[href*="emergent.sh"], a[href*="app.emergent.sh"]').forEach(el => {
    if (!el.href.includes('customer-assets')) {
      el.style.display = 'none';
      el.remove();
    }
  });
};

// Run on load and periodically
if (typeof window !== 'undefined') {
  document.addEventListener('DOMContentLoaded', removeWatermark);
  window.addEventListener('load', removeWatermark);
  setTimeout(removeWatermark, 0);
  setTimeout(removeWatermark, 100);
  setTimeout(removeWatermark, 500);
  setTimeout(removeWatermark, 1000);
  
  // Observe for dynamically added badge
  const observer = new MutationObserver(removeWatermark);
  setTimeout(() => {
    observer.observe(document.body, { childList: true, subtree: true });
  }, 100);
}

// Subdomain URLs
const SUBDOMAINS = {
  partner: "https://partner.enthida.de",
  wartung: "https://wartung.enthida.de",
  leistung: "https://leistung.enthida.de",
  checkliste: "https://checkliste.enthida.de",
  abnahme: "https://abnahme.enthida.de",
  gewerbe: "https://gewerbe.enthida.de"
};

// Logo URLs
const LOGOS = {
  hero: "https://customer-assets.emergentagent.com/job_16c261db-1a36-4883-8f20-318acd36a151/artifacts/k6b3by14_ENTHIDA%201%20Black%20v2.png",
  nav: "https://customer-assets.emergentagent.com/job_pv-abschluss/artifacts/53k6pqi6_ETHIDA%204%20LOGO%20Homepage.png",
};

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

// Navigation Component
const Navigation = ({ onOpenDownloads }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [servicesDropdownOpen, setServicesDropdownOpen] = useState(false);
  
  const navLinks = [
    { href: "#ueber-uns", label: "Über uns" },
    { href: "#leistungen", label: "Leistungen" },
    { href: "#unterschied", label: "Spezialist" },
    { href: "#partner", label: "Solarfirmen" },
    { href: "#privatkunden", label: "Privatkunden" },
    { href: "#pv-check", label: "PV-Check" },
    { href: "/ueber-mich.html", label: "Über mich" },
  ];

  const serviceLinks = [
    { href: "/pv-abnahme.html", label: "PV-Abnahme", category: "Abnahme" },
    { href: "/pv-pruefung.html", label: "PV-Prüfung", category: "Wartung" },
    { href: "/speicher-nachruesten.html", label: "Speicher Nachrüstung", category: "Wartung" },
    { href: "/smartmeter.html", label: "Smart Meter", category: "Wartung" },
    { href: "/wallbox.html", label: "Wallbox", category: "Wartung" },
    { href: "/pv-selbstbau.html", label: "Für Selberbauer", category: "Partner" },
  ];

  const scrollToSection = (e, href, external) => {
    // If it's a full page link (starts with /), let it navigate normally
    if (href.startsWith('/')) {
      setIsMenuOpen(false);
      setServicesDropdownOpen(false);
      return; // Let the default link behavior work
    }
    if (external) return; // Let external links work normally
    e.preventDefault();
    setIsMenuOpen(false);
    setServicesDropdownOpen(false);
    // Small delay to let menu close animation complete
    setTimeout(() => {
      const element = document.querySelector(href);
      if (element) {
        const yOffset = -100; // Account for fixed header
        const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
        window.scrollTo({ top: y, behavior: 'smooth' });
      }
    }, 100);
  };

  const handleDownloadsClick = (e) => {
    e.preventDefault();
    setIsMenuOpen(false);
    onOpenDownloads();
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-[70]" data-testid="navigation">
      {/* Main Navigation Bar */}
      <div className="glass">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo + Contact Info with Knight Rider effect */}
            <div className="flex items-center gap-4">
              <a href="#" className="flex items-center -ml-2 sm:-ml-3 lg:-ml-5" data-testid="nav-logo">
                <img src={LOGOS.nav} alt="ENTHIDA Logo" className="h-12 sm:h-16 lg:h-20 w-auto" />
              </a>
              {/* Contact Info with Knight Rider scanner effect - 30% larger */}
              <div className="hidden md:flex items-center gap-4 text-sm text-slate-300 border-l border-white/10 pl-6 ml-2 knight-rider-container">
                <a href="tel:+4917641234614" className="flex items-center gap-2 hover:text-neon-green transition-colors knight-rider-text whitespace-nowrap">
                  <Phone size={17} />
                  <span>0176 4123 4614</span>
                </a>
                <span className="text-white/20 mx-2">|</span>
                <a href="mailto:info@enthida.de" className="flex items-center gap-2 hover:text-neon-green transition-colors knight-rider-text whitespace-nowrap">
                  <Mail size={17} />
                  <span>info@enthida.de</span>
                </a>
              </div>
            </div>

            {/* Menu Button - Always visible */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="flex items-center gap-2 px-4 py-2 text-sm uppercase tracking-wider text-slate-300 hover:text-neon-green transition-colors border border-white/10 rounded-lg hover:border-neon-green/30"
              aria-label="Toggle menu"
              data-testid="menu-toggle"
            >
              <span className="hidden sm:inline">Menü</span>
              {isMenuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
      </div>

      {/* Slide-down Menu Bar */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="bg-dark-paper/95 backdrop-blur-md border-b border-white/10"
            data-testid="menu-drawer"
          >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
              {/* Navigation Links - Horizontal on desktop, stacked on mobile */}
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-center gap-2 lg:gap-6">
                {navLinks.map((link) => (
                  <a
                    key={link.href}
                    href={link.href}
                    onClick={(e) => scrollToSection(e, link.href, link.external)}
                    target={link.external ? "_blank" : undefined}
                    rel={link.external ? "noopener noreferrer" : undefined}
                    className="nav-link text-sm uppercase tracking-wider py-2 lg:py-0 hover:text-neon-green transition-colors"
                    data-testid={`nav-link-${link.label.toLowerCase()}`}
                  >
                    {link.label}
                  </a>
                ))}
                
                {/* Services with Dropdown */}
                <div 
                  className="relative"
                  onMouseEnter={() => setServicesDropdownOpen(true)}
                  onMouseLeave={() => setServicesDropdownOpen(false)}
                >
                  <button 
                    onClick={(e) => {
                      // Toggle dropdown
                      setServicesDropdownOpen(!servicesDropdownOpen);
                      // Scroll to "Alle Services" section
                      scrollToSection(e, '[data-testid="seo-linking-section"]', false);
                    }}
                    className="nav-link text-sm uppercase tracking-wider flex items-center gap-1 py-2 lg:py-0 cursor-pointer hover:text-neon-green transition-colors"
                    data-testid="nav-services-toggle"
                  >
                    Services
                    <ChevronDown size={14} className={`transition-transform duration-200 ${servicesDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {/* Services Dropdown */}
                  <AnimatePresence>
                    {servicesDropdownOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="lg:absolute lg:top-full lg:left-0 lg:mt-2 z-[100] w-full lg:w-64"
                      >
                        <div className="bg-dark-paper border border-white/10 shadow-2xl rounded-lg overflow-hidden mt-2 lg:mt-0">
                          <div className="py-2">
                            <div className="px-4 py-2 text-xs text-neon-green uppercase tracking-wider">Abnahme & Prüfung</div>
                            <a href="/pv-abnahme.html" className="block px-4 py-2 text-sm text-slate-300 hover:bg-white/5 hover:text-neon-green transition-colors">PV-Abnahme</a>
                            <a href="/pv-pruefung.html" className="block px-4 py-2 text-sm text-slate-300 hover:bg-white/5 hover:text-neon-green transition-colors">PV-Prüfung</a>
                            <a href="/photovoltaik-anmeldung.html" className="block px-4 py-2 text-sm text-slate-300 hover:bg-white/5 hover:text-neon-green transition-colors">PV-Anmeldung</a>
                            <a href="/bayernwerk-anmelden.html" className="block px-4 py-2 text-sm text-slate-300 hover:bg-white/5 hover:text-neon-green transition-colors">Bayernwerk Anmeldung</a>
                            <div className="border-t border-white/10 my-2"></div>
                            <div className="px-4 py-2 text-xs text-neon-green uppercase tracking-wider">Erweiterungen</div>
                            <a href="/speicher-nachruesten.html" className="block px-4 py-2 text-sm text-slate-300 hover:bg-white/5 hover:text-neon-green transition-colors">Speicher Nachrüstung</a>
                            <a href="/smartmeter.html" className="block px-4 py-2 text-sm text-slate-300 hover:bg-white/5 hover:text-neon-green transition-colors">Smart Meter</a>
                            <a href="/wallbox.html" className="block px-4 py-2 text-sm text-slate-300 hover:bg-white/5 hover:text-neon-green transition-colors">Wallbox</a>
                            <a href="/wallbox-installation.html" className="block px-4 py-2 text-sm text-slate-300 hover:bg-white/5 hover:text-neon-green transition-colors">Wallbox Installation</a>
                            <a href="/ueberschussladen.html" className="block px-4 py-2 text-sm text-slate-300 hover:bg-white/5 hover:text-neon-green transition-colors">Überschussladen</a>
                            <a href="/wechselrichter.html" className="block px-4 py-2 text-sm text-slate-300 hover:bg-white/5 hover:text-neon-green transition-colors">Wechselrichter</a>
                            <div className="border-t border-white/10 my-2"></div>
                            <div className="px-4 py-2 text-xs text-neon-green uppercase tracking-wider">Fachkompetenz</div>
                            <a href="/elektroinstallation.html" className="block px-4 py-2 text-sm text-slate-300 hover:bg-white/5 hover:text-neon-green transition-colors">Elektroinstallation</a>
                            <a href="/pv-service.html" className="block px-4 py-2 text-sm text-slate-300 hover:bg-white/5 hover:text-neon-green transition-colors">PV-Service</a>
                            <a href="/elektromeister.html" className="block px-4 py-2 text-sm text-slate-300 hover:bg-white/5 hover:text-neon-green transition-colors">Elektromeister</a>
                            <div className="border-t border-white/10 my-2"></div>
                            <div className="px-4 py-2 text-xs text-neon-green uppercase tracking-wider">Für Selberbauer</div>
                            <a href="/pv-selbstbau.html" className="block px-4 py-2 text-sm text-slate-300 hover:bg-white/5 hover:text-neon-green transition-colors">Unterstützung & Checkliste</a>
                            <div className="border-t border-white/10 my-2"></div>
                            <div className="px-4 py-2 text-xs text-neon-turquoise uppercase tracking-wider">Für Partner</div>
                            <a href={SUBDOMAINS.gewerbe} target="_blank" rel="noopener noreferrer" className="block px-4 py-2 text-sm text-slate-300 hover:bg-white/5 hover:text-neon-turquoise transition-colors flex items-center gap-2">
                              Partnerschaft für Installateure
                              <ExternalLink size={12} />
                            </a>
                            <div className="border-t border-white/10 my-2"></div>
                            <button
                              onClick={handleDownloadsClick}
                              className="w-full text-left px-4 py-2 text-sm text-slate-300 hover:bg-white/5 hover:text-neon-turquoise transition-colors flex items-center gap-2"
                            >
                              <Download size={14} />
                              Downloads & Infomaterial
                            </button>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
              
              {/* Mobile Contact Info */}
              <div className="md:hidden mt-4 pt-4 border-t border-white/10 flex flex-col gap-2">
                <a href="tel:+4917641234614" className="flex items-center gap-2 text-sm text-slate-300 hover:text-neon-green transition-colors">
                  <Phone size={14} />
                  <span>0176 4123 4614</span>
                </a>
                <a href="mailto:info@enthida.de" className="flex items-center gap-2 text-sm text-slate-300 hover:text-neon-green transition-colors">
                  <Mail size={14} />
                  <span>info@enthida.de</span>
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

// Rotating Text Component - Clean & Professional
const RotatingText = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  
  const phrases = [
    "Effizienz",
    "Nachvollziehbarkeit",
    "Transparenz",
    "Hochwertige Ausführung",
    "Intelligente Systeme",
    "Dauerhafte Sicherheit",
    "saubere Abnahmen",
    "Smarte Energie Systeme",
    "PV-Zustandsberichte",
    "technische Klarheit",
    "klare Kommunikation",
    "klare Dokumentation",
    "Kundenzufriedenheit",
    "sichere Anlagenchecks",
    "Individuelle Lösungen",
    "saubere Kundeneinweisung"
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % phrases.length);
    }, 2000);
    return () => clearInterval(interval);
  }, [phrases.length]);

  return (
    <span className="inline-block relative h-[1.2em] min-w-[200px]">
      {phrases.map((phrase, index) => (
        <motion.span
          key={phrase}
          initial={false}
          animate={{
            opacity: currentIndex === index ? 1 : 0,
            y: currentIndex === index ? 0 : 20
          }}
          transition={{
            duration: 0.4,
            ease: "easeOut"
          }}
          className={`text-neon-green whitespace-nowrap ${
            currentIndex === index ? 'relative' : 'absolute left-0 top-0'
          }`}
        >
          {phrase}
        </motion.span>
      ))}
    </span>
  );
};

// Hero Section - Professional & Balanced
const HeroSection = () => {
  return (
    <section className="hero-bg min-h-screen flex items-center pt-20 circuit-grid relative" data-testid="hero-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 w-full">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Text Content - Left */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="text-left"
          >
            {/* Main Headline */}
            <motion.div variants={fadeInUp} className="mb-6">
              <h1 className="font-sora font-bold tracking-tight text-white text-4xl md:text-5xl lg:text-6xl mb-3">
                ENTHIDA
              </h1>
              <p className="text-xl md:text-2xl text-slate-400">
                steht für <RotatingText />
              </p>
            </motion.div>
            
            {/* Value Proposition */}
            <motion.p 
              variants={fadeInUp}
              className="text-lg text-neon-turquoise font-medium mb-4"
            >
              Technischer Abschluss für Photovoltaikanlagen
            </motion.p>

            {/* Description */}
            <motion.p 
              variants={fadeInUp}
              className="text-base md:text-lg text-slate-400 leading-relaxed mb-8 max-w-lg"
            >
              Professionelle Abnahme, VDE-konforme Prüfungen und sichere Inbetriebnahme – damit Ihre Anlage normgerecht ans Netz geht.
            </motion.p>
            
            {/* CTA Buttons */}
            <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-4 mb-6">
              <a
                href={SUBDOMAINS.partner}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-3 bg-neon-green text-dark font-bold py-4 px-6 hover:bg-neon-green-hover transition-all group"
                data-testid="cta-warum-enthida"
              >
                <span>Jetzt entdecken</span>
                <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
              </a>
              
              <a
                href={SUBDOMAINS.wartung}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-3 bg-transparent border-2 border-slate-500 text-slate-300 font-semibold py-4 px-6 hover:border-white hover:text-white transition-all group"
                data-testid="cta-check"
              >
                <Search size={18} />
                <span>Anlage prüfen</span>
              </a>
            </motion.div>

            {/* B2B Link für Solarfirmen */}
            <motion.div variants={fadeInUp} className="mb-6">
              <a
                href={SUBDOMAINS.gewerbe}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 px-4 py-3 border border-neon-turquoise/30 bg-neon-turquoise/5 hover:border-neon-turquoise hover:bg-neon-turquoise/10 transition-all group"
                data-testid="hero-b2b-link"
              >
                <Handshake size={18} className="text-neon-turquoise shrink-0" />
                <span className="text-sm text-slate-300">Für <strong className="text-white font-semibold">Solarfirmen & Installateure</strong> – Partner werden</span>
                <ArrowRight size={16} className="text-neon-turquoise group-hover:translate-x-1 transition-transform shrink-0" />
              </a>
            </motion.div>

            {/* Small Helper Hint */}
            <motion.div variants={fadeInUp} className="flex items-center gap-2 text-slate-500 text-sm">
              <Zap size={14} className="text-neon-green" />
              <span>Auch für andere Elektroarbeiten erreichbar – <a href="#kontakt" className="text-slate-400 hover:text-neon-green transition-colors underline underline-offset-2">einfach melden</a></span>
            </motion.div>
          </motion.div>

          {/* Logo - Right */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="flex justify-center lg:justify-end"
          >
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-neon-green/15 to-neon-turquoise/15 blur-3xl"></div>
              <img 
                src={LOGOS.hero} 
                alt="ENTHIDA Smart Energy Systems" 
                className="relative w-64 md:w-72 lg:w-80 h-auto"
              />
            </div>
          </motion.div>
        </div>

        {/* Social Proof - Centered Transition */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-16 lg:mt-24"
        >
          <div className="flex flex-wrap items-center justify-center gap-6 sm:gap-10 lg:gap-16 py-8 border-t border-b border-slate-700/30">
            <div className="text-center">
              <span className="block text-3xl sm:text-4xl lg:text-5xl font-bold text-neon-green">180+</span>
              <span className="text-sm sm:text-base text-slate-500">Abnahmen durchgeführt</span>
            </div>
            <div className="hidden sm:block w-px h-12 bg-slate-700/50"></div>
            <div className="text-center">
              <span className="block text-3xl sm:text-4xl lg:text-5xl font-bold text-neon-turquoise">60+</span>
              <span className="text-sm sm:text-base text-slate-500">AC-seitig installiert</span>
            </div>
            <div className="hidden sm:block w-px h-12 bg-slate-700/50"></div>
            <div className="text-center">
              <span className="block text-3xl sm:text-4xl lg:text-5xl font-bold text-neon-green">6+</span>
              <span className="text-sm sm:text-base text-slate-500">Partner-Firmen</span>
            </div>
            <div className="hidden sm:block w-px h-12 bg-slate-700/50"></div>
            <div className="text-center">
              <span className="block text-3xl sm:text-4xl lg:text-5xl font-bold text-neon-turquoise flex items-center justify-center gap-2">
                <Phone size={32} className="inline" />
              </span>
              <span className="text-sm sm:text-base text-slate-500">Persönlich erreichbar</span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// SEO Content Section - Keyword-optimierter Text für Suchmaschinen
const SEOContentSection = () => {
  return (
    <section id="ueber-uns" className="section-dark py-16 border-t border-white/5" data-testid="seo-content-section">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
          className="prose prose-invert prose-lg max-w-none"
        >
          <motion.div variants={fadeInUp}>
            <h1 className="font-sora text-2xl md:text-3xl font-bold text-white mb-6">
              Photovoltaik & PV Abnahme – Ihr Elektriker in Eggenfelden
            </h1>
            <p className="text-slate-400 leading-relaxed mb-6">
              ENTHIDA ist Ihr spezialisierter Partner für den professionellen technischen Abschluss von <strong className="text-white">Photovoltaik</strong>-Anlagen. Wir gewährleisten eine normgerechte, sichere und effiziente Inbetriebnahme Ihrer Solaranlage im Raum Eggenfelden. Unser Fokus liegt auf Transparenz, hochwertiger Ausführung und maximaler Kundenzufriedenheit.
            </p>
            
            <h2 className="font-sora text-xl md:text-2xl font-semibold text-neon-green mt-10 mb-4">
              Unsere Leistungen: Mehr als nur die PV Abnahme
            </h2>
            <p className="text-slate-400 leading-relaxed mb-6">
              Als erfahrener <strong className="text-white">Elektriker in Eggenfelden</strong> bieten wir Ihnen ein komplettes Dienstleistungspaket für den sicheren Betrieb Ihrer Anlage. Von der ersten Prüfung bis zur finalen Anmeldung beim Netzbetreiber – wir kümmern uns um alle technischen und administrativen Schritte.
            </p>
            
            <h3 className="font-sora text-lg md:text-xl font-semibold text-white mt-8 mb-3">
              Professionelle PV Abnahme und Inbetriebnahme
            </h3>
            <p className="text-slate-400 leading-relaxed mb-4">
              Die fachgerechte <strong className="text-white">PV Abnahme</strong> ist entscheidend für die Sicherheit und Langlebigkeit Ihrer Investition. Unser Service beinhaltet eine detaillierte technische Prüfung aller Komponenten, einen umfassenden Funktionstest sowie die normgerechte Inbetriebnahme Ihrer Anlage. Sie erhalten ein vollständiges Inbetriebnahmeprotokoll als Nachweis der korrekten Ausführung.
            </p>
            <ul className="text-slate-400 space-y-2 mb-6 list-none pl-0">
              <li className="flex items-start gap-3">
                <CheckCircle size={20} className="text-neon-green flex-shrink-0 mt-1" />
                <span><strong className="text-white">Vollständige Anlagenprüfung:</strong> Wir kontrollieren alle Bauteile auf korrekte Installation und Funktion.</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle size={20} className="text-neon-green flex-shrink-0 mt-1" />
                <span><strong className="text-white">Prüfungen nach VDE-Norm:</strong> Messungen nach VDE 0100-600 (DC- und AC-seitig) sind bei uns Standard.</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle size={20} className="text-neon-green flex-shrink-0 mt-1" />
                <span><strong className="text-white">Sichere Inbetriebnahme:</strong> Wir stellen sicher, dass Ihre Anlage optimal und sicher ans Netz geht.</span>
              </li>
            </ul>
            
            <h3 className="font-sora text-lg md:text-xl font-semibold text-white mt-8 mb-3">
              Anmeldung und Dokumentation
            </h3>
            <p className="text-slate-400 leading-relaxed mb-6">
              Wir entlasten Sie vollständig von bürokratischen Hürden. Unser Team übernimmt die komplette Dokumentation und Registrierung Ihrer <strong className="text-white">Photovoltaik</strong>-Anlage. Dazu gehören die Fertigmeldung beim zuständigen Netzbetreiber sowie die Eintragung im Marktstammdatenregister.
            </p>
            
            <h2 className="font-sora text-xl md:text-2xl font-semibold text-neon-green mt-10 mb-4">
              Warum ENTHIDA als Ihr Elektriker Eggenfelden?
            </h2>
            <p className="text-slate-400 leading-relaxed mb-6">
              Im Gegensatz zu vielen anderen Anbietern, bei denen die <strong className="text-white">Photovoltaik</strong> nur ein Nebengeschäft ist, konzentrieren wir uns auf den technischen Abschluss von Solaranlagen. Unsere Spezialisierung garantiert Ihnen höchste Qualität, tiefgehendes Fachwissen und eine effiziente Abwicklung. Wir stehen für intelligente Systeme, transparente Kommunikation und eine saubere, verständliche Einweisung in Ihre neue Anlage.
            </p>
            
            <h3 className="font-sora text-lg md:text-xl font-semibold text-white mt-8 mb-3">
              Regelmäßige Wiederholungsprüfungen für Bestandsanlagen
            </h3>
            <p className="text-slate-400 leading-relaxed">
              Neben der Spezialisierung auf die <strong className="text-white">PV Abnahme</strong> sind wir als qualifizierter Elektromeisterbetrieb auch für andere Elektroarbeiten Ihr zuverlässiger Ansprechpartner. Kontaktieren Sie uns für individuelle Lösungen rund um die Elektrotechnik.
            </p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// Leistungen Section - Enhanced
const LeistungenSection = () => {
  const services = [
    {
      nummer: "01",
      icon: <ClipboardCheck size={36} />,
      title: "Abnahme & Inbetriebnahme",
      desc: "Technische Prüfung und sichere Inbetriebnahme Ihrer PV-Anlage",
      details: [
        "Komplette Anlagenprüfung",
        "Funktionstest aller Komponenten",
        "Inbetriebnahmeprotokoll"
      ]
    },
    {
      nummer: "02",
      icon: <FileCheck size={36} />,
      title: "Netzbetreiber-Anmeldung",
      desc: "Vollständige Dokumentation und Registrierung",
      details: [
        "Fertigmeldung beim Netzbetreiber",
        "Marktstammdatenregister",
        "Alle Formulare für Sie"
      ]
    },
    {
      nummer: "03",
      icon: <Shield size={36} />,
      title: "VDE-Prüfungen",
      desc: "Normgerechte Messungen und Prüfprotokolle",
      details: [
        "Messungen nach VDE 0100-600",
        "DC- und AC-Prüfungen",
        "Prüfprotokoll inklusive"
      ]
    },
    {
      nummer: "04",
      icon: <CheckCircle size={36} />,
      title: "Wiederholungs-prüfungen",
      desc: "Regelmäßige Sicherheitsprüfungen für Bestandsanlagen",
      details: [
        "Prüfung nach DGUV V3",
        "Alle 4 Jahre empfohlen",
        "Mängeldokumentation"
      ]
    }
  ];

  return (
    <section id="leistungen" className="section-paper py-24" data-testid="leistungen-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
        >
          <motion.div variants={fadeInUp} className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-neon-turquoise/10 border border-neon-turquoise/30 text-neon-turquoise text-sm font-medium uppercase tracking-wider mb-4">
              Unsere Leistungen
            </span>
            <h2 className="font-sora text-3xl md:text-5xl font-semibold tracking-tight text-white mb-4">
              Was wir für Sie tun
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Vom technischen Abschluss bis zur Wiederholungsprüfung – alles aus einer Hand.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="service-card p-6 group relative overflow-hidden"
              >
                {/* Nummer oben rechts */}
                <span className="absolute top-4 right-4 font-sora text-3xl font-bold text-neon-green/20 group-hover:text-neon-green/40 transition-colors">
                  {service.nummer}
                </span>
                
                {/* Inklusive Badge */}
                <span className="inline-block px-2 py-1 bg-neon-green/10 border border-neon-green/30 text-neon-green text-xs font-medium uppercase tracking-wider mb-4">
                  ✓ Inklusive
                </span>
                
                {/* Icon */}
                <div className="w-16 h-16 mb-4 flex items-center justify-center bg-neon-green/10 border border-neon-green/30 text-neon-green group-hover:bg-neon-green group-hover:text-dark transition-all">
                  {service.icon}
                </div>
                
                {/* Title */}
                <h3 className="font-sora text-lg font-semibold text-white mb-2">
                  {service.title}
                </h3>
                
                {/* Description */}
                <p className="text-slate-400 text-sm mb-4">
                  {service.desc}
                </p>
                
                {/* Details List */}
                <ul className="space-y-1">
                  {service.details.map((detail, i) => (
                    <li key={i} className="text-slate-500 text-xs flex items-center gap-2">
                      <span className="w-1 h-1 bg-neon-turquoise rounded-full"></span>
                      {detail}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>

          {/* Trust Element */}
          <motion.div variants={fadeInUp} className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-6 text-center">
            <div className="flex items-center gap-2 text-slate-400">
              <Shield size={20} className="text-neon-green" />
              <span className="text-sm">Elektromeister-Qualität</span>
            </div>
            <div className="hidden sm:block w-px h-6 bg-slate-700"></div>
            <div className="flex items-center gap-2 text-slate-400">
              <CheckCircle size={20} className="text-neon-green" />
              <span className="text-sm">VDE-konforme Prüfungen</span>
            </div>
            <div className="hidden sm:block w-px h-6 bg-slate-700"></div>
            <div className="flex items-center gap-2 text-slate-400">
              <FileCheck size={20} className="text-neon-green" />
              <span className="text-sm">Dokumentation inklusive</span>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// Der ENTHIDA Unterschied - Was uns auszeichnet
const UnterschiedSection = () => {
  const unterschiede = [
    {
      standard: "PV oft nur ein Teilbereich",
      enthida: "Fokus auf Photovoltaik",
      icon: <Zap size={24} />
    },
    {
      standard: "Abnahme nebenbei",
      enthida: "Abnahme als Schwerpunkt",
      icon: <ClipboardCheck size={24} />
    },
    {
      standard: "Dokumentation später",
      enthida: "Direkte Dokumentation",
      icon: <FileCheck size={24} />
    },
    {
      standard: "Einweisung auf Nachfrage",
      enthida: "Einweisung inklusive",
      icon: <Users size={24} />
    }
  ];

  return (
    <section id="unterschied" className="section-dark py-24 circuit-grid relative" data-testid="unterschied-section">
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-neon-green to-transparent"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
        >
          <motion.div variants={fadeInUp} className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-neon-green/10 border border-neon-green/30 text-neon-green text-sm font-medium uppercase tracking-wider mb-4">
              Der Unterschied
            </span>
            <h2 className="font-sora text-3xl md:text-5xl font-semibold tracking-tight text-white mb-4">
              Worauf wir uns konzentrieren
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto text-lg">
              ENTHIDA ist auf den technischen Abschluss von PV-Anlagen spezialisiert.
            </p>
          </motion.div>

          {/* Vergleichstabelle */}
          <motion.div variants={fadeInUp} className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-4 md:gap-0">
              {/* Header */}
              <div className="hidden md:block bg-slate-800/50 p-4 text-center border-b border-slate-700">
                <span className="text-slate-400 font-medium">Oft üblich</span>
              </div>
              <div className="hidden md:block bg-neon-green/10 p-4 text-center border-b border-neon-green/30">
                <span className="text-neon-green font-bold">Bei ENTHIDA</span>
              </div>
              
              {/* Rows */}
              {unterschiede.map((item, index) => (
                <div key={index} className="contents">
                  {/* Standard */}
                  <div className="bg-slate-800/30 p-5 flex items-center gap-4 border-b border-slate-700/50 md:border-r">
                    <div className="w-10 h-10 flex items-center justify-center bg-slate-700/50 text-slate-500">
                      <X size={20} />
                    </div>
                    <span className="text-slate-400">{item.standard}</span>
                  </div>
                  
                  {/* ENTHIDA */}
                  <div className="bg-neon-green/5 p-5 flex items-center gap-4 border-b border-neon-green/20">
                    <div className="w-10 h-10 flex items-center justify-center bg-neon-green/20 text-neon-green">
                      {item.icon}
                    </div>
                    <span className="text-white font-medium">{item.enthida}</span>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </motion.div>
      </div>
      
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-neon-turquoise to-transparent"></div>
    </section>
  );
};

// Partner Section - Prozess für Solarfirmen
const PartnerSection = () => {
  const schritte = [
    {
      nummer: "01",
      title: "Anlage montiert",
      desc: "Ihre Montage ist abgeschlossen, aber Abnahme und Dokumentation stehen noch aus.",
      icon: <ClipboardCheck size={28} />
    },
    {
      nummer: "02", 
      title: "ENTHIDA übernimmt",
      desc: "Wir kümmern uns um Abnahme, VDE-Prüfung, Dokumentation und Kundeneinweisung.",
      icon: <Shield size={28} />
    },
    {
      nummer: "03",
      title: "Projekt abgeschlossen",
      desc: "Ihr Kunde ist zufrieden, Sie konzentrieren sich bereits auf die nächste Montage.",
      icon: <CheckCircle size={28} />
    }
  ];

  return (
    <section id="partner" className="section-paper py-24" data-testid="partner-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
        >
          <motion.div variants={fadeInUp} className="text-center mb-8">
            <span className="inline-block px-4 py-2 bg-neon-turquoise/10 border border-neon-turquoise/30 text-neon-turquoise text-sm font-medium uppercase tracking-wider mb-4">
              Für Solarfirmen
            </span>
            <h2 className="font-sora text-3xl md:text-5xl font-semibold tracking-tight text-white mb-6">
              So einfach geht's
            </h2>
          </motion.div>

          {/* Zitat */}
          <motion.div variants={fadeInUp} className="max-w-3xl mx-auto mb-16">
            <div className="border-l-4 border-neon-turquoise bg-neon-turquoise/5 p-6 text-center">
              <p className="text-xl md:text-2xl text-white font-medium italic">
                „Während Ihr Team die nächste Anlage montiert, kümmert sich ENTHIDA um den technischen Abschluss."
              </p>
            </div>
          </motion.div>

          {/* 3-Schritte Prozess */}
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {schritte.map((schritt, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="relative"
              >
                {/* Verbindungslinie */}
                {index < schritte.length - 1 && (
                  <div className="hidden md:block absolute top-16 left-[60%] w-[80%] h-0.5 bg-gradient-to-r from-neon-turquoise/50 to-transparent"></div>
                )}
                
                <div className="text-center">
                  {/* Nummer */}
                  <div className="inline-flex items-center justify-center w-16 h-16 mb-6 bg-neon-turquoise/10 border-2 border-neon-turquoise text-neon-turquoise font-sora font-bold text-2xl">
                    {schritt.nummer}
                  </div>
                  
                  {/* Icon & Title */}
                  <div className="flex items-center justify-center gap-3 mb-4">
                    <span className="text-neon-green">{schritt.icon}</span>
                    <h3 className="font-sora text-xl font-semibold text-white">
                      {schritt.title}
                    </h3>
                  </div>
                  
                  {/* Description */}
                  <p className="text-slate-400 max-w-xs mx-auto">
                    {schritt.desc}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>

          {/* CTA */}
          <motion.div variants={fadeInUp} className="text-center flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a
              href="https://partner.enthida.de/fuer-solarfirmen"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-neon-turquoise text-dark font-bold py-5 px-10 text-lg hover:bg-neon-turquoise-hover transition-all hover:scale-105 shadow-lg shadow-neon-turquoise/25"
            >
              <Handshake size={24} />
              Jetzt Partnerschaft anfragen
              <ArrowRight size={22} />
            </a>
            <a
              href={SUBDOMAINS.gewerbe}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-transparent border-2 border-neon-green text-neon-green font-bold py-5 px-10 text-lg hover:bg-neon-green/10 transition-all hover:scale-105"
            >
              <Users size={24} />
              Für Installateure
              <ArrowRight size={22} />
            </a>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// Selbstbauer / Privatkunden Section
const SelbstbauerSection = () => {
  return (
    <section id="privatkunden" className="section-dark py-24 relative" data-testid="selbstbauer-section">
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-neon-green to-transparent"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
        >
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Text Content */}
            <motion.div variants={fadeInUp}>
              <span className="inline-block px-4 py-2 bg-neon-green/10 border border-neon-green/30 text-neon-green text-sm font-medium uppercase tracking-wider mb-4">
                Für Privatkunden
              </span>
              <h2 className="font-sora text-3xl md:text-4xl font-semibold tracking-tight text-white mb-6">
                Anlage selbst gebaut?
              </h2>
              
              <div className="space-y-4 text-slate-400 text-lg leading-relaxed mb-8">
                <p>
                  Du hast deine PV-Anlage selbst installiert – vielleicht mit Hilfe eines Freundes, 
                  Nachbarn oder Bekannten, der Elektriker ist?
                </p>
                <p>
                  <span className="text-white font-medium">Kein Problem.</span> Für die offizielle Abnahme, 
                  VDE-Prüfung und Inbetriebnahme sind wir da.
                </p>
              </div>

              <a
                href="https://partner.enthida.de/fuer-privatkunden"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 bg-neon-green text-dark font-bold py-4 px-8 hover:bg-neon-green-hover transition-all hover:scale-105"
              >
                <Home size={20} />
                Jetzt Anfrage stellen
                <ArrowRight size={20} />
              </a>
            </motion.div>

            {/* Feature List */}
            <motion.div variants={fadeInUp}>
              <div className="bg-dark-paper border border-neon-green/30 p-8">
                <h3 className="font-sora text-xl font-semibold text-neon-green mb-6">
                  Was wir für dich übernehmen
                </h3>
                <ul className="space-y-4">
                  {[
                    "Technische Prüfung deiner Installation",
                    "VDE-konforme Messungen & Protokolle",
                    "Unterstützung bei Netzbetreiber-Anmeldung",
                    "Registrierung im Marktstammdatenregister",
                    "Einweisung in dein neues Energiesystem"
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-4">
                      <div className="w-6 h-6 flex items-center justify-center bg-neon-green/20 text-neon-green flex-shrink-0 mt-0.5">
                        <CheckCircle size={14} />
                      </div>
                      <span className="text-slate-300">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Prüfungs Section
const PruefungsSection = () => {
  return (
    <section id="pv-check" className="section-dark py-24 relative" data-testid="pruefung-section">
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-white/30 to-transparent"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
          className="text-center"
        >
          <motion.div variants={fadeInUp}>
            <span className="inline-block px-4 py-2 bg-white/10 border border-white/30 text-white text-sm font-medium uppercase tracking-wider mb-4">
              PV-Check
            </span>
            <h2 className="font-sora text-3xl md:text-5xl font-semibold tracking-tight text-white mb-6">
              Klarheit statt Unsicherheit
            </h2>
            <p className="text-slate-400 max-w-3xl mx-auto mb-8 text-lg">
              Für Immobilienbesitzer, Käufer und Betreiber: Lassen Sie Ihre Bestandsanlage 
              professionell bewerten. Unser PV-Check gibt Ihnen Transparenz über Zustand, 
              Sicherheit und Optimierungspotenzial.
            </p>
          </motion.div>
          
          <motion.div variants={fadeInUp} className="flex flex-wrap justify-center gap-4">
            <a
              href={SUBDOMAINS.wartung}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-white text-dark font-bold py-4 px-8 hover:bg-slate-200 transition-all"
            >
              <Search size={20} />
              Anlage prüfen lassen
              <ExternalLink size={16} />
            </a>
          </motion.div>
          
          <motion.div variants={fadeInUp} className="mt-12 grid sm:grid-cols-3 gap-6 max-w-3xl mx-auto">
            {[
              { label: "Zustandsbewertung", desc: "Technische Analyse" },
              { label: "Sicherheitscheck", desc: "VDE-konforme Prüfung" },
              { label: "Optimierungstipps", desc: "Verbesserungspotenzial" }
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="text-2xl font-bold text-white mb-1">{item.label}</div>
                <div className="text-slate-500 text-sm">{item.desc}</div>
              </div>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// SEO Internal Linking Section - Verbindung zu Subdomains
const SEOLinkingSection = () => {
  const [showAllMobile, setShowAllMobile] = useState(false);
  const MOBILE_VISIBLE = 6;
  const linkItems = [
    {
      title: "PV-Abnahme & Netzanschluss",
      description: "Unterstützung bei der technischen PV-Abnahme, Dokumentation, Kundeneinweisung und Fertigstellung. Elektroinstallation und Netzanschluss für Privatkunden und Solarfirmen.",
      buttonText: "Mehr Infos",
      buttonLink: "/pv-abnahme.html",
      linkTitle: "PV-Abnahme & Netzanschluss vom Elektromeister | ENTHIDA",
      icon: <FileCheck size={32} />,
      color: "neon-green",
      highlight: true
    },
    {
      title: "PV Prüfung & Anlagenbewertung",
      description: "Professionelle PV Prüfung, technische Anlagenbewertung und Sichtprüfung für Photovoltaikanlagen. Prüfungen von Wechselrichtern, Schutzmaßnahmen und sicherheitsrelevanten Komponenten.",
      buttonText: "Mehr Infos",
      buttonLink: "/pv-pruefung.html",
      linkTitle: "Professionelle PV Prüfung in Eggenfelden | ENTHIDA",
      icon: <Search size={32} />,
      color: "turquoise"
    },
    {
      title: "Photovoltaik Anmeldung",
      description: "Technische Unterstützung bei Anmeldung, Dokumentation und Fertigstellung von Photovoltaikanlagen für Privatkunden und Solarfirmen.",
      buttonText: "Mehr Infos",
      buttonLink: "/photovoltaik-anmeldung.html",
      linkTitle: "Photovoltaik Anmeldung & Netzanschluss | ENTHIDA",
      icon: <FileCheck size={32} />,
      color: "spring-green"
    },
    {
      title: "Bayernwerk Anmeldung",
      description: "Unterstützung bei technischen Unterlagen, Anmeldung und Kommunikation mit dem Netzbetreiber Bayernwerk.",
      buttonText: "Mehr Infos",
      buttonLink: "/bayernwerk-anmelden.html",
      linkTitle: "Bayernwerk Anmeldung für PV-Anlagen | ENTHIDA",
      icon: <FileCheck size={32} />,
      color: "sky-blue"
    },
    {
      title: "Speicher Nachrüstung",
      description: "Nachrüstung von Batteriespeichern für bestehende Photovoltaikanlagen. Energiespeicher-Integration, Optimierung des Eigenverbrauchs und Erweiterung bestehender Systeme.",
      buttonText: "Mehr Infos",
      buttonLink: "/speicher-nachruesten.html",
      linkTitle: "Photovoltaik Speicher Nachrüstung | ENTHIDA Eggenfelden",
      icon: <Zap size={32} />,
      color: "yellow"
    },
    {
      title: "Smart Meter Installation",
      description: "Smart Meter Installation, Anschluss und Integration in Photovoltaikanlagen. Wechselrichter-Einbindung und intelligente Strommessung vom Elektrofachbetrieb.",
      buttonText: "Mehr Infos",
      buttonLink: "/smartmeter.html",
      linkTitle: "Smart Meter Installation vom Elektrofachbetrieb | ENTHIDA",
      icon: <Activity size={32} />,
      color: "orange-blue-white"
    },
    {
      title: "Wallbox & Überschussladen",
      description: "Wallbox Installation für Elektrofahrzeuge mit Überschussladen-Funktion. Elektroinstallation, Absicherung und Integration in Ihre Photovoltaikanlage.",
      buttonText: "Mehr Infos",
      buttonLink: "/wallbox.html",
      linkTitle: "Wallbox Installation & Überschussladen | ENTHIDA",
      icon: <Car size={32} />,
      color: "green-blue-turquoise"
    },
    {
      title: "Wallbox Installation",
      description: "Professionelle Unterstützung bei der Wallbox Installation für private und gewerbliche Anwendungen inklusive technischer Integration in bestehende Energiesysteme.",
      buttonText: "Mehr Infos",
      buttonLink: "/wallbox-installation.html",
      linkTitle: "Wallbox Installation vom Elektromeister | ENTHIDA",
      icon: <Car size={32} />,
      color: "emerald"
    },
    {
      title: "Überschussladen mit PV",
      description: "Optimierung des Eigenverbrauchs durch intelligentes Überschussladen und smarte Steuerung von Wallboxen und Energiesystemen.",
      buttonText: "Mehr Infos",
      buttonLink: "/ueberschussladen.html",
      linkTitle: "Überschussladen mit Photovoltaik | ENTHIDA",
      icon: <Zap size={32} />,
      color: "orange"
    },
    {
      title: "Wechselrichter Prüfung",
      description: "Unterstützung bei Wechselrichterprüfung, technischer Bewertung und Integration in bestehende Photovoltaikanlagen.",
      buttonText: "Mehr Infos",
      buttonLink: "/wechselrichter.html",
      linkTitle: "Wechselrichter Prüfung & Installation | ENTHIDA",
      icon: <Activity size={32} />,
      color: "blue"
    },
    {
      title: "Elektroinstallation",
      description: "Unterstützung bei moderner Elektroinstallation, Energiesystemen und technischer Integration von Photovoltaik- und Smart-Home-Komponenten.",
      buttonText: "Mehr Infos",
      buttonLink: "/elektroinstallation.html",
      linkTitle: "Elektroinstallation für moderne Energietechnik | ENTHIDA",
      icon: <Zap size={32} />,
      color: "purple"
    },
    {
      title: "PV-Service & Wartung",
      description: "Technische Unterstützung, Wartung und Serviceleistungen für Photovoltaikanlagen und Energiesysteme.",
      buttonText: "Mehr Infos",
      buttonLink: "/pv-service.html",
      linkTitle: "PV-Service & technische Betreuung | ENTHIDA",
      icon: <Search size={32} />,
      color: "teal"
    },
    {
      title: "Elektromeister",
      description: "Technische Unterstützung durch einen Elektromeister für Photovoltaikanlagen, Wallboxen, Smart Meter und moderne Energiesysteme.",
      buttonText: "Mehr Infos",
      buttonLink: "/elektromeister.html",
      linkTitle: "Elektromeister für moderne Energietechnik | ENTHIDA",
      icon: <Users size={32} />,
      color: "pink"
    },
    {
      title: "Unterstützung für Selberbauer",
      description: "Technische Unterstützung für Personen, die ihre Photovoltaikanlage selbst montieren. Hilfe bei Prüfung, Abnahme und Netzanschluss durch unseren Elektrofachbetrieb.",
      buttonText: "Mehr Infos",
      buttonLink: "/pv-selbstbau.html",
      linkTitle: "PV-Selbstbau Unterstützung vom Elektromeister | ENTHIDA",
      icon: <Users size={32} />,
      color: "purple",
      extraButton: {
        text: "Checkliste",
        link: "https://checkliste.enthida.de"
      }
    }
  ];

  return (
    <section className="relative py-20 overflow-hidden" data-testid="seo-linking-section">
      {/* Background with subtle gradient accent */}
      <div className="absolute inset-0 bg-dark-paper">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-neon-green/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-neon-turquoise/5 rounded-full blur-3xl"></div>
      </div>
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
        >
          <motion.div variants={fadeInUp} className="text-center mb-12">
            <span className="inline-block px-4 py-2 bg-neon-green/10 border border-neon-green/30 text-neon-green text-sm font-medium uppercase tracking-wider mb-4">
              Alle Services
            </span>
            <h2 className="font-sora text-3xl md:text-4xl font-semibold tracking-tight text-white mb-4">
              Unsere spezialisierten Leistungen
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Vom Elektrofachbetrieb: Elektroinstallation, Abnahme, Wartung und Erweiterung Ihrer Photovoltaikanlage.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {linkItems.map((item, index) => {
              // Color configurations for each theme
              const colorConfig = {
                'neon-green': {
                  border: 'border-white/10 hover:border-neon-green/30 hover:shadow-[0_0_20px_rgba(0,255,127,0.1)]',
                  icon: 'bg-neon-green/10 border-neon-green/30 text-neon-green',
                  button: 'bg-neon-green hover:bg-neon-green-hover text-dark'
                },
                'turquoise': {
                  border: 'border-neon-turquoise/20 hover:border-neon-turquoise/50 hover:shadow-[0_0_20px_rgba(0,206,209,0.15)]',
                  icon: 'bg-neon-turquoise/10 border-neon-turquoise/30 text-neon-turquoise',
                  button: 'bg-neon-turquoise hover:bg-neon-turquoise/80 text-dark'
                },
                'yellow': {
                  border: 'border-yellow-400/20 hover:border-yellow-400/50 hover:shadow-[0_0_20px_rgba(250,204,21,0.15)]',
                  icon: 'bg-yellow-400/10 border-yellow-400/30 text-yellow-400',
                  button: 'bg-gradient-to-r from-yellow-400 to-amber-500 hover:from-yellow-300 hover:to-amber-400 text-dark'
                },
                'orange': {
                  border: 'border-orange-400/20 hover:border-orange-400/50 hover:shadow-[0_0_20px_rgba(251,146,60,0.15)]',
                  icon: 'bg-gradient-to-br from-orange-400/20 to-blue-500/10 border-orange-400/30 text-orange-400',
                  button: 'bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-400 hover:to-amber-400 text-dark'
                },
                'orange-blue-white': {
                  border: 'border-orange-400/20 hover:border-white/40 hover:shadow-[0_0_20px_rgba(255,255,255,0.1)]',
                  icon: 'bg-gradient-to-br from-orange-400/20 via-blue-500/10 to-white/10 border-orange-400/30 text-orange-400',
                  button: 'bg-gradient-to-r from-orange-500 via-blue-500 to-white hover:from-orange-400 hover:via-blue-400 hover:to-slate-100 text-dark'
                },
                'green-blue-turquoise': {
                  border: 'border-neon-green/20 hover:border-neon-turquoise/50 hover:shadow-[0_0_20px_rgba(0,206,209,0.15)]',
                  icon: 'bg-gradient-to-br from-neon-green/20 via-blue-500/10 to-neon-turquoise/20 border-neon-green/30 text-neon-green',
                  button: 'bg-gradient-to-r from-neon-green via-blue-500 to-neon-turquoise hover:from-neon-green/80 hover:via-blue-400 hover:to-neon-turquoise/80 text-dark'
                },
                'purple': {
                  border: 'border-purple-400/20 hover:border-purple-400/50 hover:shadow-[0_0_20px_rgba(192,132,252,0.15)]',
                  icon: 'bg-gradient-to-br from-purple-500/20 to-green-400/10 border-purple-400/30 text-purple-400',
                  button: 'bg-gradient-to-r from-purple-500 via-green-400 to-orange-400 hover:from-purple-400 hover:via-green-300 hover:to-orange-300 text-dark'
                },
                'spring-green': {
                  border: 'border-emerald-400/20 hover:border-emerald-400/50 hover:shadow-[0_0_20px_rgba(52,211,153,0.15)]',
                  icon: 'bg-emerald-400/10 border-emerald-400/30 text-emerald-400',
                  button: 'bg-gradient-to-r from-emerald-400 to-cyan-400 hover:from-emerald-300 hover:to-cyan-300 text-dark'
                },
                'sky-blue': {
                  border: 'border-sky-400/20 hover:border-sky-400/50 hover:shadow-[0_0_20px_rgba(56,189,248,0.15)]',
                  icon: 'bg-sky-400/10 border-sky-400/30 text-sky-400',
                  button: 'bg-gradient-to-r from-sky-400 to-cyan-400 hover:from-sky-300 hover:to-cyan-300 text-dark'
                },
                'emerald': {
                  border: 'border-green-500/20 hover:border-green-500/50 hover:shadow-[0_0_20px_rgba(34,197,94,0.15)]',
                  icon: 'bg-green-500/10 border-green-500/30 text-green-500',
                  button: 'bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-400 hover:to-emerald-400 text-dark'
                },
                'blue': {
                  border: 'border-blue-500/20 hover:border-blue-500/50 hover:shadow-[0_0_20px_rgba(59,130,246,0.15)]',
                  icon: 'bg-blue-500/10 border-blue-500/30 text-blue-500',
                  button: 'bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-400 hover:to-indigo-400 text-white'
                },
                'teal': {
                  border: 'border-teal-400/20 hover:border-teal-400/50 hover:shadow-[0_0_20px_rgba(45,212,191,0.15)]',
                  icon: 'bg-teal-400/10 border-teal-400/30 text-teal-400',
                  button: 'bg-gradient-to-r from-teal-400 to-cyan-400 hover:from-teal-300 hover:to-cyan-300 text-dark'
                },
                'pink': {
                  border: 'border-pink-400/20 hover:border-pink-400/50 hover:shadow-[0_0_20px_rgba(244,114,182,0.15)]',
                  icon: 'bg-pink-400/10 border-pink-400/30 text-pink-400',
                  button: 'bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-400 hover:to-rose-400 text-white'
                }
              };
              
              const colors = colorConfig[item.color] || colorConfig['neon-green'];
              
              return (
                <motion.div
                  key={index}
                  variants={fadeInUp}
                  className={`relative bg-dark border p-6 transition-all duration-300 group hover:translate-y-[-4px] ${
                    index >= MOBILE_VISIBLE && !showAllMobile ? 'hidden md:block' : ''
                  } ${
                    item.highlight 
                      ? 'border-neon-green/50 shadow-[0_0_30px_rgba(0,255,127,0.15)]' 
                      : colors.border
                  }`}
                  itemScope
                  itemType="https://schema.org/Service"
                >
                  {/* Highlight badge */}
                  {item.highlight && (
                    <div className="absolute -top-3 left-6 px-3 py-1 bg-neon-green text-dark text-xs font-bold uppercase tracking-wider">
                      Beliebt
                    </div>
                  )}
                  
                  <div className={`w-12 h-12 flex items-center justify-center mb-4 ${colors.icon} border group-hover:scale-110 transition-transform`}>
                    {item.icon}
                  </div>
                  <h3 className="font-sora text-lg font-semibold text-white mb-2" itemProp="name">
                    {item.title}
                  </h3>
                  <p className="text-slate-400 text-sm leading-relaxed mb-5" itemProp="description">
                    {item.description}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <a
                      href={item.buttonLink}
                      title={item.linkTitle}
                      itemProp="url"
                      className={`inline-flex items-center gap-2 font-semibold py-2.5 px-5 text-sm transition-all group/btn ${colors.button}`}
                    >
                      <span>{item.buttonText}</span>
                      <ArrowRight size={16} className="group-hover/btn:translate-x-1 transition-transform" />
                    </a>
                    {item.extraButton && (
                      <a
                        href={item.extraButton.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white font-semibold py-2.5 px-5 text-sm transition-all"
                      >
                        <span>{item.extraButton.text}</span>
                      </a>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Mobile: Alle Leistungen ein-/ausklappen */}
          <div className="md:hidden mt-8 text-center">
            <button
              onClick={() => setShowAllMobile(!showAllMobile)}
              className="inline-flex items-center gap-2 px-6 py-3 border border-neon-green/40 bg-neon-green/10 text-neon-green font-semibold text-sm uppercase tracking-wider hover:bg-neon-green hover:text-dark transition-all"
              data-testid="seo-show-all-toggle"
            >
              <span>{showAllMobile ? 'Weniger anzeigen' : `Alle ${linkItems.length} Leistungen anzeigen`}</span>
              <ChevronDown size={16} className={`transition-transform ${showAllMobile ? 'rotate-180' : ''}`} />
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Downloads Modal - Opens on Click
const DownloadsModal = ({ isOpen, onClose }) => {
  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] flex items-center justify-center"
      data-testid="downloads-modal"
    >
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-dark/95 backdrop-blur-md"
        onClick={onClose}
      />
      
      {/* Modal Content */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.3, ease: "easeOut" }}
        className="relative w-full max-w-6xl max-h-[90vh] overflow-y-auto mx-4 p-8 md:p-12"
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 md:top-8 md:right-8 w-12 h-12 flex items-center justify-center bg-slate-800/50 border border-white/10 text-slate-400 hover:text-white hover:border-neon-green transition-all z-10"
          aria-label="Schließen"
        >
          <X size={24} />
        </button>

        {/* Header */}
        <div className="text-center mb-12">
          <span className="inline-block px-4 py-2 bg-neon-turquoise/10 border border-neon-turquoise/30 text-neon-turquoise text-sm font-medium uppercase tracking-wider mb-4">
            Infomaterial
          </span>
          <h2 className="font-sora text-3xl md:text-5xl font-semibold tracking-tight text-white mb-4">
            Downloads
          </h2>
          <p className="text-slate-400 max-w-2xl mx-auto text-lg">
            Informationen zum Mitnehmen – für Partner und Kunden.
          </p>
        </div>

        {/* Download Cards Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          
          {/* Card 1: Leistungsumfang B2B - ACTIVE */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <a
              href={SUBDOMAINS.leistung}
              target="_blank"
              rel="noopener noreferrer"
              className="block h-full bg-slate-800/50 border-2 border-neon-turquoise/30 p-6 text-center hover:border-neon-turquoise hover:bg-slate-800/80 transition-all group cursor-pointer"
            >
              <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center bg-neon-turquoise/10 border border-neon-turquoise/30 text-neon-turquoise group-hover:scale-110 group-hover:bg-neon-turquoise/20 transition-all">
                <Building2 size={40} />
              </div>
              <h3 className="font-sora text-lg font-semibold text-white mb-1">
                Leistungsumfang B2B
              </h3>
              <span className="text-neon-turquoise text-sm font-medium block mb-3">
                Für Solarfirmen
              </span>
              <p className="text-slate-400 text-sm mb-4">
                Komplettes Leistungsverzeichnis für professionelle Partner
              </p>
              <div className="inline-flex items-center gap-2 text-neon-turquoise font-medium text-sm group-hover:gap-3 transition-all">
                <ExternalLink size={18} />
                <span>Mehr erfahren</span>
              </div>
            </a>
          </motion.div>

          {/* Card 2: Checkliste Selbstbauer - ACTIVE */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <a
              href={SUBDOMAINS.checkliste}
              target="_blank"
              rel="noopener noreferrer"
              className="block h-full bg-slate-800/50 border-2 border-neon-green/30 p-6 text-center hover:border-neon-green hover:bg-slate-800/80 transition-all group cursor-pointer"
            >
              <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center bg-neon-green/10 border border-neon-green/30 text-neon-green group-hover:scale-110 group-hover:bg-neon-green/20 transition-all">
                <ClipboardCheck size={40} />
              </div>
              <h3 className="font-sora text-lg font-semibold text-white mb-1">
                Checkliste Selbstbauer
              </h3>
              <span className="text-neon-green text-sm font-medium block mb-3">
                Für Privatkunden
              </span>
              <p className="text-slate-400 text-sm mb-4">
                Was Sie für die Abnahme vorbereiten sollten
              </p>
              <div className="inline-flex items-center gap-2 text-neon-green font-medium text-sm group-hover:gap-3 transition-all">
                <ExternalLink size={18} />
                <span>Mehr erfahren</span>
              </div>
            </a>
          </motion.div>

          {/* Card 3: Partnerinfo Installateure - ACTIVE */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <a
              href={SUBDOMAINS.gewerbe}
              target="_blank"
              rel="noopener noreferrer"
              className="block h-full bg-slate-800/50 border-2 border-neon-turquoise/30 p-6 text-center hover:border-neon-turquoise hover:bg-slate-800/80 transition-all group cursor-pointer"
            >
              <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center bg-neon-turquoise/10 border border-neon-turquoise/30 text-neon-turquoise group-hover:scale-110 group-hover:bg-neon-turquoise/20 transition-all">
                <Handshake size={40} />
              </div>
              <h3 className="font-sora text-lg font-semibold text-white mb-1">
                Partnerinfo Installateure
              </h3>
              <span className="text-neon-turquoise text-sm font-medium block mb-3">
                Für Elektrobetriebe
              </span>
              <p className="text-slate-400 text-sm mb-4">
                Zusammenarbeit und Kooperationsmöglichkeiten
              </p>
              <div className="inline-flex items-center gap-2 text-neon-turquoise font-medium text-sm group-hover:gap-3 transition-all">
                <ExternalLink size={18} />
                <span>Mehr erfahren</span>
              </div>
            </a>
          </motion.div>

          {/* Card 5: Netzanmeldungen - COMING SOON */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="sm:col-span-2 lg:col-span-2"
          >
            <div className="block h-full bg-slate-800/30 border-2 border-slate-700/50 p-6 opacity-70">
              <div className="flex flex-col lg:flex-row gap-6 items-center lg:items-start text-center lg:text-left">
                <div className="w-20 h-20 flex-shrink-0 flex items-center justify-center bg-slate-700/30 border border-slate-600/30 text-slate-500">
                  <FileCheck size={40} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-center lg:justify-start gap-3 mb-2">
                    <h3 className="font-sora text-lg font-semibold text-slate-400">
                      Netzanmeldungen
                    </h3>
                    <span className="px-2 py-1 bg-slate-700/50 text-slate-500 text-xs font-medium uppercase">
                      Demnächst
                    </span>
                  </div>
                  <span className="text-slate-500 text-sm font-medium block mb-3">
                    Für Firmen ohne Meister
                  </span>
                  <p className="text-slate-500 text-sm mb-3">
                    Vollständige, strukturierte Abwicklung aller Netzanschluss-Prozesse – von der Ersteinreichung bis zur Freigabe.
                  </p>
                  <ul className="text-slate-500 text-xs space-y-1">
                    <li>• Ausfüllen und Einreichen von Netzbetreiber-Anträgen (E.1, E.2, E.8)</li>
                    <li>• Prüfung der Projektunterlagen auf Vollständigkeit</li>
                    <li>• Kommunikation mit Netzbetreibern bei Rückfragen</li>
                    <li>• Unterstützung beim Marktstammdatenregister</li>
                  </ul>
                </div>
              </div>
            </div>
          </motion.div>

        </div>

        {/* Footer hints */}
        <div className="text-center mt-8">
          <p className="text-slate-500 text-sm">
            Klicke außerhalb oder drücke <kbd className="px-2 py-1 bg-slate-800 border border-slate-700 text-slate-400 text-xs">ESC</kbd> zum Schließen
          </p>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Bewertungen Section - Trustlocal Widget mit elegantem Design
const BewertungenSection = () => {
  useEffect(() => {
    // Load Trustlocal widget script
    const existingScript = document.querySelector('script[src*="trustlocal"]');
    if (!existingScript) {
      const script = document.createElement('script');
      script.src = 'https://static.trustlocal.de/widget/widget_v2.js';
      script.async = true;
      document.body.appendChild(script);
    }
  }, []);

  return (
    <section className="relative py-24 overflow-hidden" data-testid="bewertungen-section">
      {/* Gradient Background - Übergang von dunkel zu paper */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#0a0f1a] via-[#0f1729] to-[#111827]"></div>
      
      {/* Subtle glow effects */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-neon-green/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-neon-turquoise/5 rounded-full blur-3xl"></div>
      
      {/* Top border line */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent"></div>
      
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
        >
          {/* Header */}
          <motion.div variants={fadeInUp} className="text-center mb-12">
            <span className="inline-block px-4 py-1.5 bg-neon-green/10 border border-neon-green/20 text-neon-green text-xs font-semibold uppercase tracking-wider mb-6">
              Kundenstimmen
            </span>
            <h2 className="font-sora text-3xl md:text-4xl font-semibold tracking-tight text-white mb-4">
              Vertrauen durch <span className="text-neon-turquoise">Erfahrung</span>
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Was unsere Kunden und Partner über die Zusammenarbeit mit ENTHIDA sagen
            </p>
          </motion.div>

          {/* Main Content - Quote + Widget */}
          <motion.div variants={fadeInUp} className="grid md:grid-cols-2 gap-8 items-center">
            
            {/* Left: Testimonial Quote */}
            <div className="relative">
              <div className="absolute -top-4 -left-2 text-6xl text-neon-green/20 font-serif">"</div>
              <blockquote className="bg-white/[0.02] backdrop-blur-sm border border-white/5 rounded-2xl p-8 relative">
                <p className="text-lg text-slate-300 leading-relaxed mb-6 italic">
                  Endlich ein Elektriker, der versteht was er tut. Die Abnahme war professionell, 
                  die Dokumentation vollständig und die Kommunikation einwandfrei. 
                  Kann ich nur weiterempfehlen!
                </p>
                <footer className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-neon-green/20 to-neon-turquoise/20 flex items-center justify-center border border-white/10">
                    <span className="text-neon-green font-semibold">MK</span>
                  </div>
                  <div>
                    <cite className="text-white font-medium not-italic block">Markus K.</cite>
                    <span className="text-slate-500 text-sm">PV-Anlage 12 kWp, Eggenfelden</span>
                  </div>
                </footer>
              </blockquote>
            </div>

            {/* Right: Widget in elegant frame */}
            <div className="flex flex-col items-center">
              <div className="bg-white/[0.02] backdrop-blur-sm border border-white/5 rounded-2xl p-6 w-full max-w-sm">
                <div 
                  className="trustlocal-widget flex justify-center" 
                  data-id="joMcKaaopoq0eHVqtX5jSW3z8WKwvXHx45MqD64qeEpRkg" 
                  data-language-code="de" 
                  data-country-code="DE" 
                  data-badge="hidden" 
                  data-quote="default" 
                  data-size="large" 
                  data-type="portrait" 
                  data-border="hidden" 
                  data-theme="dark" 
                  data-background="default" 
                  data-google="default"
                ></div>
              </div>
              <p className="text-slate-500 text-sm mt-4 text-center">
                Verifizierte Bewertungen auf Google & Trustlocal
              </p>
            </div>
          </motion.div>

          {/* Trust indicators */}
          <motion.div variants={fadeInUp} className="mt-16 flex flex-wrap justify-center gap-8 text-center">
            <div className="flex items-center gap-2 text-slate-400">
              <CheckCircle size={18} className="text-neon-green" />
              <span className="text-sm">Meisterbetrieb</span>
            </div>
            <div className="flex items-center gap-2 text-slate-400">
              <CheckCircle size={18} className="text-neon-green" />
              <span className="text-sm">180+ Abnahmen</span>
            </div>
            <div className="flex items-center gap-2 text-slate-400">
              <CheckCircle size={18} className="text-neon-green" />
              <span className="text-sm">100% Weiterempfehlung</span>
            </div>
          </motion.div>
        </motion.div>
      </div>
      
      {/* Bottom gradient fade to next section */}
      <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-[#111827] to-transparent"></div>
    </section>
  );
};

// Contact Section - Final CTA
const ContactSection = () => {
  const whatsappNumber = "4917641234614";
  const whatsappMessage = encodeURIComponent("Hallo, ich interessiere mich für Ihre PV-Dienstleistungen.");
  const whatsappLink = `https://wa.me/${whatsappNumber}?text=${whatsappMessage}`;

  return (
    <section id="kontakt" className="section-paper py-24 relative overflow-hidden" data-testid="contact-section">
      {/* Background Glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-neon-green/5 rounded-full blur-3xl"></div>
      
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
        >
          {/* Header */}
          <motion.div variants={fadeInUp} className="text-center mb-12">
            <h2 className="font-sora text-3xl md:text-5xl font-semibold tracking-tight text-white mb-4">
              Bereit für den nächsten Schritt?
            </h2>
            <p className="text-xl text-slate-400">
              Hi, schreib mir direkt für deine Anfrage zu PV-Abnahme, Inbetriebnahme oder Prüfung.
            </p>
          </motion.div>
          
          {/* Contact Grid */}
          <motion.div variants={fadeInUp}>
            <div className="grid md:grid-cols-3 gap-6">
              {/* WhatsApp */}
              <a 
                href={whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                className="group bg-[#25D366]/10 border-2 border-[#25D366]/50 p-8 text-center hover:bg-[#25D366]/20 hover:border-[#25D366] transition-all"
              >
                <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center bg-[#25D366] text-white group-hover:scale-110 transition-transform">
                  <svg className="w-10 h-10" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                </div>
                <span className="text-[#25D366] font-bold text-xl block mb-1">WhatsApp</span>
                <p className="text-white text-sm">Antwort noch am selben Tag</p>
              </a>

              {/* Telefon */}
              <a 
                href="tel:+4917641234614"
                className="group bg-neon-turquoise/10 border-2 border-neon-turquoise/50 p-8 text-center hover:bg-neon-turquoise/20 hover:border-neon-turquoise transition-all"
              >
                <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center bg-neon-turquoise text-dark group-hover:scale-110 transition-transform">
                  <Phone size={40} />
                </div>
                <span className="text-neon-turquoise font-bold text-xl block mb-2">Anrufen</span>
                <p className="text-white text-xl font-bold tracking-wide">+49 176 41234 614</p>
              </a>

              {/* Email */}
              <a 
                href="mailto:info@enthida.de?subject=Anfrage%20über%20Website"
                className="group bg-neon-green/10 border-2 border-neon-green/50 p-8 text-center hover:bg-neon-green/20 hover:border-neon-green transition-all"
              >
                <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center bg-neon-green text-dark group-hover:scale-110 transition-transform">
                  <Mail size={40} />
                </div>
                <span className="text-neon-green font-bold text-xl block mb-2">E-Mail</span>
                <p className="text-white text-lg font-semibold">info@enthida.de</p>
              </a>
            </div>
          </motion.div>

          {/* Footer Info */}
          <motion.div variants={fadeInUp} className="mt-12 text-center">
            <div className="inline-flex items-center gap-2 px-6 py-3 bg-dark border border-white/10 text-slate-400">
              <MapPin size={18} className="text-neon-green" />
              <span>Einsatzgebiet: <span className="text-white font-medium">Eggenfelden + 100km</span> / Bayern</span>
            </div>
            
            <div className="mt-8">
              <p className="text-neon-green font-semibold text-lg">ENTHIDA – Smart Energy Systems</p>
              <p className="text-slate-500">Elektromeister Enes Dedic</p>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// FAQ Section - sichtbares Pendant zum FAQ-Schema in index.html
const FAQ_ITEMS = [
  {
    cat: "Kosten",
    q: "Was kostet eine PV-Abnahme bei ENTHIDA?",
    a: "Eine PV-Abnahme inklusive VDE-Prüfung und Prüfprotokoll ist ab ca. 400 € möglich. Der genaue Preis hängt von Anlagengröße und Prüfumfang ab – der Wert ist daher keine Preisgarantie, sondern ein Richtwert zur Orientierung. Ihr individuelles Angebot erhalten Sie transparent und ohne versteckte Kosten – ganz unkompliziert per E-Mail an info@enthida.de, telefonisch oder per WhatsApp."
  },
  {
    cat: "Prüfung & Technik",
    q: "Meine Anlage wurde von einer anderen Firma montiert – übernimmt ENTHIDA trotzdem die Abnahme?",
    a: "Ja – genau das ist unsere Spezialität. Wir übernehmen Abnahme, VDE-Prüfung, Dokumentation und Netzbetreiber-Anmeldung auch für Anlagen, die von anderen Firmen oder in Eigenleistung montiert wurden. Voraussetzung ist eine fachgerechte Montage, die wir im Rahmen der Prüfung bewerten."
  },
  {
    cat: "Anmeldung",
    q: "Wie lange dauert die Anmeldung meiner PV-Anlage beim Netzbetreiber?",
    a: "Die Anmeldung beim Netzbetreiber (z.B. Bayernwerk) dauert in der Regel 2-4 Wochen. ENTHIDA übernimmt die komplette Kommunikation mit dem Netzbetreiber – von der Einspeiseanfrage über die technische Prüfung bis zur Fertigmeldung und Zählersetzung."
  },
  {
    cat: "Anmeldung",
    q: "Brauche ich einen Elektromeister für meine PV-Anlage?",
    a: "Ja, für die Inbetriebnahme und Anmeldung einer PV-Anlage beim Netzbetreiber ist ein eingetragener Elektrofachbetrieb erforderlich. Enes Dedic, Inhaber von ENTHIDA, ist Elektromeister und in die Handwerksrolle der Handwerkskammer Niederbayern-Oberpfalz eingetragen."
  },
  {
    cat: "Anmeldung",
    q: "Was passiert, wenn ich meine PV-Anlage nicht anmelde?",
    a: "Die Registrierung im Marktstammdatenregister ist gesetzlich verpflichtend. Wer seine Anlage nicht anmeldet, riskiert Bußgelder und verliert den Anspruch auf Einspeisevergütung. Auch der Netzbetreiber kann die Einspeisung untersagen. ENTHIDA übernimmt die komplette Anmeldung für Sie – korrekt und fristgerecht."
  },
  {
    cat: "Prüfung & Technik",
    q: "Was ist bei einer PV-Prüfung nach VDE 0100-600 enthalten?",
    a: "Eine VDE-konforme PV-Prüfung umfasst: Sichtprüfung der gesamten Anlage, Messung des Isolationswiderstands, Prüfung der Schutzmaßnahmen, Funktionsprüfung aller Komponenten sowie die Erstellung eines vollständigen Prüfprotokolls für den Netzbetreiber."
  },
  {
    cat: "Prüfung & Technik",
    q: "Wie oft sollte eine PV-Anlage geprüft werden?",
    a: "Empfohlen wird eine Wiederholungsprüfung alle 4 Jahre, angelehnt an die DGUV Vorschrift 3. Für gewerbliche Anlagen ist sie verpflichtend, für private Anlagen dringend empfohlen – Witterungseinflüsse, Alterung oder Nagetierschäden bleiben sonst oft unbemerkt. Eine regelmäßige Prüfung sichert Ertrag, Versicherungsschutz und Betriebssicherheit."
  },
  {
    cat: "Erweiterung & Trends",
    q: "Kann ich meine bestehende PV-Anlage mit einem Speicher nachrüsten?",
    a: "Ja, in den meisten Fällen ist eine Speicher-Nachrüstung möglich. Übliche Heimspeicher liegen je nach Verbrauch und Anlagengröße zwischen 5 und 15 kWh. ENTHIDA prüft die Kompatibilität Ihrer bestehenden Anlage und berät Sie zur optimalen Speicherlösung. So erhöhen Sie Ihren Eigenverbrauch und werden unabhängiger vom Stromnetz."
  },
  {
    cat: "Erweiterung & Trends",
    q: "Bietet ENTHIDA auch Wallbox-Installation für E-Autos an?",
    a: "Ja, wir installieren Wallboxen (in der Regel mit 11 kW Ladeleistung) fachgerecht und normkonform. Auf Wunsch richten wir auch Überschussladen ein, damit Ihr Elektroauto bevorzugt mit selbst erzeugtem Solarstrom geladen wird. Die Installation erfolgt inklusive Anmeldung beim Netzbetreiber."
  },
  {
    cat: "Erweiterung & Trends",
    q: "Lohnt sich ein Balkonkraftwerk?",
    a: "Für die meisten Haushalte: ja. Ein Balkonkraftwerk mit bis zu 800 Watt Wechselrichterleistung darf seit dem Solarpaket I vereinfacht betrieben werden – es genügt die Registrierung im Marktstammdatenregister. Bei Anschaffungskosten von ca. 400-800 € amortisiert sich die Anlage häufig in 3 bis 6 Jahren. Wichtig ist eine sichere Montage und ein fachgerechter Anschluss – dabei unterstützen wir Sie gern."
  }
];

const FAQ_CATEGORIES = ["Alle", "Kosten", "Anmeldung", "Prüfung & Technik", "Erweiterung & Trends"];

const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(null);
  const [activeCat, setActiveCat] = useState("Alle");

  const filteredFaqs = activeCat === "Alle"
    ? FAQ_ITEMS
    : FAQ_ITEMS.filter((f) => f.cat === activeCat);

  const catColors = {
    "Kosten": "text-yellow-400 border-yellow-400/30 bg-yellow-400/10",
    "Anmeldung": "text-neon-turquoise border-neon-turquoise/30 bg-neon-turquoise/10",
    "Prüfung & Technik": "text-neon-green border-neon-green/30 bg-neon-green/10",
    "Erweiterung & Trends": "text-purple-400 border-purple-400/30 bg-purple-400/10"
  };

  return (
    <section id="faq" className="relative py-24 overflow-hidden bg-dark" data-testid="faq-section">
      {/* Background: Grid + Glow Orbs + Watermark */}
      <div className="absolute inset-0 circuit-grid opacity-60"></div>
      <div className="absolute top-20 -left-32 w-96 h-96 bg-neon-green/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-20 -right-32 w-[500px] h-[500px] bg-neon-turquoise/5 rounded-full blur-3xl"></div>
      <div className="absolute top-8 right-4 lg:right-16 font-sora font-bold text-[180px] lg:text-[280px] leading-none text-white/[0.02] select-none pointer-events-none" aria-hidden="true">
        ?
      </div>

      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
        >
          <motion.div variants={fadeInUp} className="text-center mb-10">
            <span className="inline-block px-4 py-2 bg-neon-green/10 border border-neon-green/30 text-neon-green text-sm font-medium uppercase tracking-wider mb-4">
              FAQ
            </span>
            <h2 className="font-sora text-3xl md:text-5xl font-semibold tracking-tight text-white mb-4">
              Häufige Fragen
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Konkrete Antworten mit echten Anhaltspunkten – rund um Kosten, Anmeldung, Prüfung und PV-Trends.
            </p>
          </motion.div>

          {/* Kategorie-Filter */}
          <motion.div variants={fadeInUp} className="flex flex-wrap justify-center gap-2 mb-10">
            {FAQ_CATEGORIES.map((cat) => (
              <button
                key={cat}
                onClick={() => { setActiveCat(cat); setOpenIndex(null); }}
                className={`px-4 py-2 text-xs sm:text-sm font-medium uppercase tracking-wider border transition-all ${
                  activeCat === cat
                    ? 'bg-neon-green text-dark border-neon-green'
                    : 'bg-white/[0.03] text-slate-400 border-white/10 hover:border-neon-green/40 hover:text-white'
                }`}
                data-testid={`faq-filter-${cat.toLowerCase().replace(/[^a-z]+/g, '-')}`}
              >
                {cat}
                <span className={`ml-2 ${activeCat === cat ? 'text-dark/60' : 'text-slate-600'}`}>
                  {cat === "Alle" ? FAQ_ITEMS.length : FAQ_ITEMS.filter((f) => f.cat === cat).length}
                </span>
              </button>
            ))}
          </motion.div>

          <div className="space-y-4">
            {filteredFaqs.map((faq, index) => {
              const isOpen = openIndex === index;
              return (
                <motion.div
                  key={faq.q}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                  className={`relative backdrop-blur-md transition-all duration-300 ${
                    isOpen
                      ? 'bg-white/[0.04] border border-neon-green/40 shadow-[0_0_25px_rgba(0,255,127,0.08)]'
                      : 'bg-white/[0.02] border border-white/10 hover:border-white/25'
                  }`}
                >
                  {/* Neon-Akzent links bei geöffnetem Item */}
                  <div className={`absolute left-0 top-0 bottom-0 w-[3px] transition-opacity duration-300 bg-gradient-to-b from-neon-green to-neon-turquoise ${isOpen ? 'opacity-100' : 'opacity-0'}`}></div>

                  <button
                    onClick={() => setOpenIndex(isOpen ? null : index)}
                    className="w-full flex items-center justify-between gap-4 text-left p-5 md:p-6"
                    aria-expanded={isOpen}
                    data-testid={`faq-question-${index}`}
                  >
                    <div className="flex-1 min-w-0">
                      <span className={`hidden sm:inline-block px-2 py-0.5 mb-2 text-[10px] font-medium uppercase tracking-wider border ${catColors[faq.cat]}`}>
                        {faq.cat}
                      </span>
                      <span className={`block font-sora font-semibold text-base md:text-lg transition-colors ${
                        isOpen ? 'text-neon-green' : 'text-white'
                      }`}>
                        {faq.q}
                      </span>
                    </div>
                    <div className={`shrink-0 w-9 h-9 flex items-center justify-center border transition-all duration-300 ${
                      isOpen
                        ? 'bg-neon-green border-neon-green text-dark rotate-180'
                        : 'bg-transparent border-white/15 text-slate-500'
                    }`}>
                      <ChevronDown size={18} />
                    </div>
                  </button>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.25, ease: "easeInOut" }}
                        className="overflow-hidden"
                      >
                        <p className="px-5 md:px-6 pb-5 md:pb-6 text-slate-400 leading-relaxed text-sm md:text-base max-w-3xl">
                          {faq.a}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </div>

          <motion.p variants={fadeInUp} className="mt-10 text-center text-slate-500 text-sm">
            Ihre Frage war nicht dabei?{" "}
            <a href="#kontakt" className="text-neon-green hover:underline underline-offset-4">
              Kontaktieren Sie uns direkt
            </a>
          </motion.p>
        </motion.div>
      </div>
    </section>
  );
};

// Footer
const Footer = ({ onOpenImpressum, onOpenDatenschutz }) => {
  const footerServices = [
    { href: "/pv-abnahme.html", label: "PV-Abnahme & Netzanschluss" },
    { href: "/pv-pruefung.html", label: "PV-Prüfung" },
    { href: "/photovoltaik-anmeldung.html", label: "Photovoltaik Anmeldung" },
    { href: "/speicher-nachruesten.html", label: "Speicher Nachrüstung" },
    { href: "/wallbox.html", label: "Wallbox & Überschussladen" },
    { href: "/smartmeter.html", label: "Smart Meter Installation" },
  ];

  return (
    <footer className="section-dark pt-16 pb-8 border-t border-white/10" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-10 mb-12">
          {/* Brand */}
          <div>
            <img src={LOGOS.nav} alt="ENTHIDA" className="h-12 w-auto mb-4" />
            <p className="text-slate-400 text-sm leading-relaxed mb-4">
              Technischer Abschluss für Photovoltaikanlagen – Abnahme, VDE-Prüfung und Netzbetreiber-Anmeldung vom Elektromeisterbetrieb.
            </p>
            <div className="flex items-center gap-2 text-slate-500 text-sm">
              <MapPin size={15} className="text-neon-green shrink-0" />
              <span>Einsatzgebiet: Eggenfelden + 100 km</span>
            </div>
          </div>

          {/* Kontakt */}
          <div>
            <h3 className="font-sora text-white font-semibold text-sm uppercase tracking-wider mb-4">Kontakt</h3>
            <ul className="space-y-3 text-sm">
              <li>
                <a href="tel:+4917641234614" className="flex items-center gap-2 text-slate-400 hover:text-neon-green transition-colors">
                  <Phone size={15} className="text-neon-green shrink-0" />
                  0176 4123 4614
                </a>
              </li>
              <li>
                <a href="mailto:info@enthida.de" className="flex items-center gap-2 text-slate-400 hover:text-neon-green transition-colors">
                  <Mail size={15} className="text-neon-green shrink-0" />
                  info@enthida.de
                </a>
              </li>
              <li className="flex items-start gap-2 text-slate-400">
                <MapPin size={15} className="text-neon-green shrink-0 mt-0.5" />
                <span>Ludwig-Stempfl-Straße 17<br />84307 Eggenfelden</span>
              </li>
            </ul>
          </div>

          {/* Leistungen */}
          <div>
            <h3 className="font-sora text-white font-semibold text-sm uppercase tracking-wider mb-4">Leistungen</h3>
            <ul className="space-y-2.5 text-sm">
              {footerServices.map((service) => (
                <li key={service.href}>
                  <a href={service.href} className="text-slate-400 hover:text-neon-green transition-colors">
                    {service.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Für Partner & Rechtliches */}
          <div>
            <h3 className="font-sora text-white font-semibold text-sm uppercase tracking-wider mb-4">Für Solarfirmen</h3>
            <ul className="space-y-2.5 text-sm mb-6">
              <li>
                <a
                  href={SUBDOMAINS.gewerbe}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-neon-turquoise hover:text-white transition-colors"
                >
                  <Handshake size={15} className="shrink-0" />
                  Partner werden
                </a>
              </li>
              <li>
                <a href="/pv-selbstbau.html" className="text-slate-400 hover:text-neon-green transition-colors">
                  Unterstützung für Selberbauer
                </a>
              </li>
            </ul>
            <h3 className="font-sora text-white font-semibold text-sm uppercase tracking-wider mb-4">Rechtliches</h3>
            <ul className="space-y-2.5 text-sm">
              <li>
                <button onClick={onOpenImpressum} className="text-slate-400 hover:text-neon-green transition-colors">
                  Impressum
                </button>
              </li>
              <li>
                <button onClick={onOpenDatenschutz} className="text-slate-400 hover:text-neon-green transition-colors">
                  Datenschutz
                </button>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-slate-500 text-sm">
            © {new Date().getFullYear()} ENTHIDA – Smart Energy Systems
          </p>
          <p className="text-slate-600 text-xs">
            Elektromeister Enes Dedic · Eggenfelden, Bayern
          </p>
        </div>
      </div>
    </footer>
  );
};

// Impressum Modal
const ImpressumModal = ({ isOpen, onClose }) => {
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] flex items-center justify-center"
    >
      <div className="absolute inset-0 bg-dark/95 backdrop-blur-md" onClick={onClose} />
      
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="relative w-full max-w-3xl max-h-[90vh] overflow-y-auto mx-4 bg-slate-900/90 border border-white/10 p-8 md:p-12"
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center bg-slate-800/50 border border-white/10 text-slate-400 hover:text-white hover:border-neon-green transition-all"
        >
          <X size={20} />
        </button>

        <h2 className="font-sora text-3xl font-bold text-white mb-8">Impressum</h2>
        
        <div className="space-y-6 text-slate-300">
          <div>
            <h3 className="text-neon-green font-semibold mb-2">Angaben gemäß § 5 TMG</h3>
            <p>ENTHIDA – Smart Energy Systems</p>
            <p className="mt-2"><strong>Inhaber:</strong></p>
            <p>Enes Dedic</p>
            <p className="mt-2"><strong>Anschrift:</strong></p>
            <p>Ludwig-Stempfl-Straße 17</p>
            <p>84307 Eggenfelden</p>
            <p>Deutschland</p>
          </div>

          <div>
            <h3 className="text-neon-green font-semibold mb-2">Kontakt</h3>
            <p>Telefon: +49 176 41234614</p>
            <p>E-Mail: info@enthida.de</p>
          </div>

          <div>
            <h3 className="text-neon-green font-semibold mb-2">Angaben zur Berufsbezeichnung</h3>
            <p>Berufsbezeichnung: Elektrotechnikerhandwerk</p>
            <p>(verliehen in Deutschland)</p>
          </div>

          <div>
            <h3 className="text-neon-green font-semibold mb-2">Eintragung in die Handwerksrolle</h3>
            <p>Eingetragen in die Handwerksrolle der Handwerkskammer Niederbayern-Oberpfalz</p>
          </div>

          <div>
            <h3 className="text-neon-green font-semibold mb-2">Umsatzsteuer</h3>
            <p>Steuernummer: 113/210/40669</p>
            <p>Umsatzsteuer-Identifikationsnummer gemäß § 27a UStG:</p>
            <p className="font-semibold">DE462189160</p>
          </div>

          <div>
            <h3 className="text-neon-green font-semibold mb-2">Verantwortlich für den Inhalt nach § 18 Abs. 2 MStV</h3>
            <p>Enes Dedic</p>
            <p>Ludwig-Stempfl-Straße 17</p>
            <p>84307 Eggenfelden</p>
          </div>

          <div>
            <h3 className="text-neon-green font-semibold mb-2">Streitschlichtung</h3>
            <p className="text-sm text-slate-400">
              Die Europäische Kommission stellt eine Plattform zur Online-Streitbeilegung (OS) bereit: 
              <a href="https://ec.europa.eu/consumers/odr/" target="_blank" rel="noopener noreferrer" className="text-neon-turquoise hover:underline ml-1">
                https://ec.europa.eu/consumers/odr/
              </a>
            </p>
            <p className="text-sm text-slate-400 mt-2">
              Wir sind nicht bereit oder verpflichtet, an Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle teilzunehmen.
            </p>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Datenschutz Modal
const DatenschutzModal = ({ isOpen, onClose }) => {
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] flex items-center justify-center"
    >
      <div className="absolute inset-0 bg-dark/95 backdrop-blur-md" onClick={onClose} />
      
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="relative w-full max-w-3xl max-h-[90vh] overflow-y-auto mx-4 bg-slate-900/90 border border-white/10 p-8 md:p-12"
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center bg-slate-800/50 border border-white/10 text-slate-400 hover:text-white hover:border-neon-green transition-all"
        >
          <X size={20} />
        </button>

        <h2 className="font-sora text-3xl font-bold text-white mb-8">Datenschutzerklärung</h2>
        
        <div className="space-y-6 text-slate-300 text-sm">
          <div>
            <h3 className="text-neon-green font-semibold mb-2 text-base">1. Verantwortlicher</h3>
            <p>ENTHIDA – Smart Energy Systems</p>
            <p>Enes Dedic</p>
            <p>Ludwig-Stempfl-Straße 17</p>
            <p>84307 Eggenfelden</p>
            <p>Deutschland</p>
            <p className="mt-2">E-Mail: info@enthida.de</p>
            <p>Telefon: +49 176 41234614</p>
          </div>

          <div>
            <h3 className="text-neon-green font-semibold mb-2 text-base">2. Hosting</h3>
            <p>Diese Website wird extern gehostet. Die personenbezogenen Daten, die auf dieser Website erfasst werden, werden auf den Servern des Hosters gespeichert. Hierbei kann es sich v.a. um IP-Adressen, Kontaktanfragen, Meta- und Kommunikationsdaten, Vertragsdaten, Kontaktdaten, Namen, Websitezugriffe und sonstige Daten, die über eine Website generiert werden, handeln.</p>
          </div>

          <div>
            <h3 className="text-neon-green font-semibold mb-2 text-base">3. Allgemeine Hinweise</h3>
            <p>Die Betreiber dieser Seiten nehmen den Schutz Ihrer persönlichen Daten sehr ernst. Wir behandeln Ihre personenbezogenen Daten vertraulich und entsprechend den gesetzlichen Datenschutzvorschriften sowie dieser Datenschutzerklärung.</p>
            <p className="mt-2">Die Nutzung unserer Website ist in der Regel ohne Angabe personenbezogener Daten möglich.</p>
          </div>

          <div>
            <h3 className="text-neon-green font-semibold mb-2 text-base">4. Datenerfassung auf dieser Website</h3>
            <p><strong>Server-Log-Dateien:</strong> Der Provider der Seiten erhebt und speichert automatisch Informationen in so genannten Server-Log-Dateien, die Ihr Browser automatisch an uns übermittelt. Dies sind:</p>
            <ul className="list-disc list-inside mt-2 space-y-1 text-slate-400">
              <li>Browsertyp und Browserversion</li>
              <li>verwendetes Betriebssystem</li>
              <li>Referrer URL</li>
              <li>Hostname des zugreifenden Rechners</li>
              <li>Uhrzeit der Serveranfrage</li>
              <li>IP-Adresse</li>
            </ul>
          </div>

          <div>
            <h3 className="text-neon-green font-semibold mb-2 text-base">5. Kontaktaufnahme</h3>
            <p>Wenn Sie uns per E-Mail, Telefon oder WhatsApp kontaktieren, wird Ihre Anfrage inklusive aller daraus hervorgehenden personenbezogenen Daten (Name, Anfrage) zum Zwecke der Bearbeitung Ihres Anliegens bei uns gespeichert und verarbeitet.</p>
          </div>

          <div>
            <h3 className="text-neon-green font-semibold mb-2 text-base">6. Externe Links</h3>
            <p>Diese Website enthält Links zu externen Diensten (WhatsApp, Telefon, E-Mail). Beim Klicken auf diese Links verlassen Sie unsere Website. Für den Datenschutz auf diesen externen Plattformen sind deren Betreiber verantwortlich.</p>
          </div>

          <div>
            <h3 className="text-neon-green font-semibold mb-2 text-base">7. Ihre Rechte</h3>
            <p>Sie haben jederzeit das Recht auf unentgeltliche Auskunft über Ihre gespeicherten personenbezogenen Daten, deren Herkunft und Empfänger und den Zweck der Datenverarbeitung sowie ein Recht auf Berichtigung oder Löschung dieser Daten.</p>
          </div>

          <div>
            <h3 className="text-neon-green font-semibold mb-2 text-base">8. Cookies</h3>
            <p>Diese Website verwendet keine Tracking-Cookies. Es werden nur technisch notwendige Cookies verwendet, die für den Betrieb der Website erforderlich sind.</p>
          </div>
        </div>

        <p className="mt-8 text-xs text-slate-500">
          Stand: Dezember 2024
        </p>
      </motion.div>
    </motion.div>
  );
};

// Main App
function App() {
  const [showDownloads, setShowDownloads] = useState(false);
  const [showImpressum, setShowImpressum] = useState(false);
  const [showDatenschutz, setShowDatenschutz] = useState(false);

  // Handle hash navigation (for /#leistungen, /#privatkunden etc. from subpages)
  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      // Wait for React to render, then scroll to the section
      setTimeout(() => {
        // Map hash to corresponding element
        const hashToSelector = {
          '#services': '[data-testid="seo-linking-section"]',
          '#leistungen': '#leistungen',
          '#unterschied': '#unterschied',
          '#partner': '#partner',
          '#privatkunden': '#privatkunden',
          '#pv-check': '#pv-check',
        };
        
        const selector = hashToSelector[hash] || hash;
        const element = document.querySelector(selector);
        
        if (element) {
          const yOffset = -100; // Account for fixed header
          const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
          window.scrollTo({ top: y, behavior: 'smooth' });
        }
      }, 500);
    }
  }, []);

  return (
    <div className="min-h-screen bg-dark">
      <Navigation onOpenDownloads={() => setShowDownloads(true)} />
      <main>
        <HeroSection />
        <SEOContentSection />
        <LeistungenSection />
        <UnterschiedSection />
        <PartnerSection />
        <SelbstbauerSection />
        <PruefungsSection />
        <SEOLinkingSection />
        <FAQSection />
        <BewertungenSection />
        <ContactSection />
      </main>
      <Footer 
        onOpenImpressum={() => setShowImpressum(true)}
        onOpenDatenschutz={() => setShowDatenschutz(true)}
      />
      
      {/* Downloads Modal */}
      <DownloadsModal 
        isOpen={showDownloads} 
        onClose={() => setShowDownloads(false)} 
      />
      
      {/* Impressum Modal */}
      <ImpressumModal
        isOpen={showImpressum}
        onClose={() => setShowImpressum(false)}
      />
      
      {/* Datenschutz Modal */}
      <DatenschutzModal
        isOpen={showDatenschutz}
        onClose={() => setShowDatenschutz(false)}
      />
      
      {/* DSGVO Cookie Consent Banner - GA4 wird erst nach Zustimmung geladen */}
      <CookieConsentBanner />
    </div>
  );
}

export default App;
